import { getEntityReferenceTokens } from '../../TeamChat.entityReferences.utils';
import {
  getMessageId,
  getMessageSender,
  getMessageText,
  isFileMessage,
} from '../../TeamChat.utils';

const addUnique = (list, value) => (!value || list.includes(value) ? list : [...list, value]);

const getMessageEntityRefs = message => {
  if (isFileMessage(message)) {
    return [];
  }

  const text = getMessageText(message);

  if (!text) {
    return [];
  }

  return getEntityReferenceTokens(text);
};

const upsertEntityReferenceBucket = (
  byType,
  { entityType, entityId, label, senderId, messageId },
) => {
  const entitiesById = byType[entityType] ?? {};
  const existing = entitiesById[entityId] ?? {
    entityType,
    entityId,
    label: '',
    senderIds: [],
    messageIds: [],
  };

  const nextBucket = {
    ...existing,
    label: label || existing.label,
    senderIds: addUnique(existing.senderIds, senderId),
    messageIds: addUnique(existing.messageIds, messageId),
  };

  return { ...byType, [entityType]: { ...entitiesById, [entityId]: nextBucket } };
};

const withCounts = entitiesById =>
  Object.fromEntries(
    Object.entries(entitiesById).map(([entityId, bucket]) => [
      entityId,
      { ...bucket, count: bucket.messageIds.length },
    ]),
  );

/**
 * Client-side entity-reference index (DEV-5448), built from the messages ALREADY LOADED in the
 * teamChat store — a deliberate stand-in until the server aggregate (DEV-5447) is deployed. Shape:
 * `{ [entityType]: { [entityId]: { entityType, entityId, label, count, senderIds[], messageIds[] } } }`.
 * `count` is the number of DISTINCT messages referencing the entity (not raw token occurrences), and
 * only covers loaded messages (the most recent page, TEAM_CHAT_MESSAGES_PAGE_SIZE) — exactly what
 * the user sees in the open thread, not the full channel history.
 */
export const buildEntityReferenceIndex = messages =>
  Object.fromEntries(
    Object.entries(
      (messages ?? []).reduce((byType, message) => {
        const messageId = getMessageId(message);
        const { userId: senderId } = getMessageSender(message);

        return getMessageEntityRefs(message).reduce(
          (acc, { entityType, entityId, label }) =>
            upsertEntityReferenceBucket(acc, { entityType, entityId, label, senderId, messageId }),
          byType,
        );
      }, {}),
    ).map(([entityType, entitiesById]) => [entityType, withCounts(entitiesById)]),
  );

export const messageReferencesEntity = (message, { entityType, entityId }) =>
  getMessageEntityRefs(message).some(
    segment => segment.entityType === entityType && segment.entityId === entityId,
  );
